import { WHITE } from './paint.js'

/**
 * The crest next to a player's name: the shapes it can take and how each one is drawn.
 *
 * The ids are the server's (config/crests.php, sent with the settings): a shape is a geometry, never
 * a colour — the colours are the paint's own, one per zone, in the order the player put them.
 */

/** How many zones each shape divides the badge into. */
export const CREST_SHAPES = {
  solid:        1,
  per_pale:     2,
  per_fess:     2,
  per_bend:     2,
  pale:         2,
  fess:         2,
  saltire:      2,
  cross:        2,
  tierced_pale: 3,
  tierced_fess: 3,
  two_stripes:  3,
  pale_duo:     3,
  saltire_duo:  3,
  cross_duo:    3,
  pale_tri:     4,
  quarterly:    4,
}

/** A band across the badge, transparent on both sides whatever the badge's size. */
const stripe = (angle, colour) =>
  `linear-gradient(${angle}deg, transparent 42%, ${colour} 42% 58%, transparent 58%)`

/** One drawing per shape: the colours in zone order become the badge's CSS. */
const DRAWINGS = {
  solid: ([colour]) => ({ backgroundColor: colour }),

  per_pale:     ([first, second]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 50%, ${second} 50%)` }),
  per_fess:     ([first, second]) => ({ backgroundImage: `linear-gradient(180deg, ${first} 50%, ${second} 50%)` }),
  per_bend:     ([first, second]) => ({ backgroundImage: `linear-gradient(135deg, ${first} 50%, ${second} 50%)` }),

  pale:         ([first, second]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 0 33%, ${second} 33% 67%, ${first} 67%)` }),
  fess:         ([first, second]) => ({ backgroundImage: `linear-gradient(180deg, ${first} 0 33%, ${second} 33% 67%, ${first} 67%)` }),

  saltire:      ([first, second]) => ({ backgroundColor: first, backgroundImage: `${stripe(45, second)}, ${stripe(-45, second)}` }),
  cross:        ([first, second]) => ({ backgroundColor: first, backgroundImage: `${stripe(90, second)}, ${stripe(0, second)}` }),

  saltire_duo:  ([first, second, third]) => ({ backgroundColor: first, backgroundImage: `${stripe(45, second)}, ${stripe(-45, third)}` }),
  cross_duo:    ([first, second, third]) => ({ backgroundColor: first, backgroundImage: `${stripe(90, second)}, ${stripe(0, third)}` }),

  tierced_pale: ([first, second, third]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 0 33.4%, ${second} 33.4% 66.7%, ${third} 66.7%)` }),
  tierced_fess: ([first, second, third]) => ({ backgroundImage: `linear-gradient(180deg, ${first} 0 33.4%, ${second} 33.4% 66.7%, ${third} 66.7%)` }),

  two_stripes:  ([first, second, third]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 0 22%, ${second} 22% 36%, ${first} 36% 64%, ${third} 64% 78%, ${first} 78%)` }),

  // The two bands of `two_stripes` brought together in the middle, wider than the background they
  // split (half the badge, so each band is a quarter and the two edges a quarter each).
  pale_duo:     ([first, second, third]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 0 25%, ${second} 25% 50%, ${third} 50% 75%, ${first} 75%)` }),

  // The two bands of `pale_duo` split by a thin stripe of its own, as in the `band_tri` livery.
  pale_tri:     ([first, second, third, fourth]) => ({ backgroundImage: `linear-gradient(90deg, ${first} 0 25%, ${second} 25% 44%, ${third} 44% 56%, ${fourth} 56% 75%, ${first} 75%)` }),

  quarterly:    ([first, second, third, fourth]) => ({ backgroundImage: `conic-gradient(from 0deg, ${first} 0 25%, ${second} 25% 50%, ${third} 50% 75%, ${fourth} 75%)` }),
}

/** The zones of a shape; one for anything this file does not draw. */
function crestZones(shape)
{
  return CREST_SHAPES[shape] ?? 1
}

/** The shapes a paint of `count` colours can fill. */
export function crestsFor(count)
{
  return Object.keys(CREST_SHAPES).filter((shape) => CREST_SHAPES[shape] <= count)
}

/** The order a shape starts from: colour 1, 2, 3... in the paint's own order. */
export function defaultOrder(zones)
{
  return Array.from({ length: zones }, (_, index) => index + 1)
}

/** Whether an order names each of a shape's zones once, and nothing else. */
export function isCrestOrder(order, zones)
{
  if (!Array.isArray(order) || order.length !== zones) {
    return false
  }

  return [...order].sort((a, b) => a - b).every((index, position) => index === position + 1)
}

/** The crest a paint of `count` colours can wear: the stored one while it fits, the plain one otherwise. */
export function resolveCrest(crest, count)
{
  const shape = crestsFor(count).includes(crest?.shape) ? crest.shape : 'solid'
  const zones = CREST_SHAPES[shape]

  return { shape, order: isCrestOrder(crest?.order, zones) ? [...crest.order] : defaultOrder(zones) }
}

/** The colours of an order, taken from the paint (`colors[0]` is colour 1). */
export function crestColors(order, colors)
{
  return order.map((index) => colors[index - 1] ?? WHITE)
}

/** The CSS that draws a crest, given its colours in zone order. */
export function crestStyle(shape, colors)
{
  return (DRAWINGS[shape] ?? DRAWINGS.solid)(fill(colors, crestZones(shape)))
}

/** The "rotate" step: every colour moves on by one zone, the last one back to the first. */
export function rotateOrder(order)
{
  return order.length > 1 ? [...order.slice(1), order[0]] : [...order]
}

/** The "swap" step: the first two zones trade places, which with the rotation reaches every order. */
export function swapOrder(order)
{
  if (order.length < 2) {
    return [...order]
  }

  const [first, second, ...rest] = order

  return [second, first, ...rest]
}

/** The colours a shape needs, repeating the first when the paint has fewer than its zones. */
function fill(colors, zones)
{
  const kept = colors.slice(0, zones)

  while (kept.length < zones) {
    kept.push(kept[0] ?? WHITE)
  }

  return kept
}
